import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { Shield, Award, Trash2, Edit, Building2, Plus } from 'lucide-react';
import type { Plumber } from '../../types';
import { PlumberForm } from '../../components/forms/PlumberForm';

export function PlumberManagement() {
  const [plumbers, setPlumbers] = useState<Plumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlumber, setEditingPlumber] = useState<Plumber | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultPlumber = {
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    license_number: '',
    insurance_info: '',
    years_experience: 0,
    service_areas: [],
    services: []
  };

  useEffect(() => {
    loadPlumbers();
  }, []);

  const loadPlumbers = async () => {
    try {
      const { data, error } = await supabase
        .from('plumbers')
        .select(`
          *,
          service_areas:plumber_service_areas(zip_code),
          services:plumber_services(service_id),
          reviews(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlumbers(data as Plumber[]);
    } catch (error) {
      console.error('Error loading plumbers:', error);
      toast.error('Failed to load plumbers');
    } finally {
      setLoading(false);
    }
  };

  const toggleVerification = async (plumber: Plumber) => {
    try {
      const { error } = await supabase
        .from('plumbers')
        .update({ is_verified: !plumber.is_verified })
        .eq('id', plumber.id);

      if (error) throw error;
      
      toast.success(`${plumber.business_name} ${
        plumber.is_verified ? 'unverified' : 'verified'
      }`);
      
      loadPlumbers();
    } catch (error) {
      console.error('Error toggling verification:', error);
      toast.error('Failed to update verification status');
    }
  };

  const toggleFeatured = async (plumber: Plumber) => {
    try {
      const { error } = await supabase
        .from('plumbers')
        .update({ is_featured: !plumber.is_featured })
        .eq('id', plumber.id);

      if (error) throw error;
      
      toast.success(`${plumber.business_name} ${
        plumber.is_featured ? 'removed from' : 'marked as'
      } featured`);
      
      loadPlumbers();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  const deletePlumber = async (plumber: Plumber) => {
    if (!confirm(`Are you sure you want to delete ${plumber.business_name}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('plumbers')
        .delete()
        .eq('id', plumber.id);

      if (error) throw error;
      
      toast.success(`${plumber.business_name} deleted`);
      loadPlumbers();
    } catch (error) {
      console.error('Error deleting plumber:', error);
      toast.error('Failed to delete plumber');
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlumber) return;
    if (!editingPlumber.service_areas?.length) {
      toast.error('Please select at least one service area');
      return;
    }

    setIsSubmitting(true);
    try {
      // First update the plumber's basic info
      const { error } = await supabase
        .from('plumbers')
        .update({
          business_name: editingPlumber.business_name,
          contact_name: editingPlumber.contact_name,
          email: editingPlumber.email,
          phone: editingPlumber.phone,
          website: editingPlumber.website,
          description: editingPlumber.description,
          license_number: editingPlumber.license_number,
          insurance_info: editingPlumber.insurance_info,
          years_experience: editingPlumber.years_experience,
        })
        .eq('id', editingPlumber.id);

      if (error) throw error;

      // Then update service areas with city relationships
      if (editingPlumber.service_areas) {
        // Delete existing service areas
        await supabase
          .from('plumber_service_areas')
          .delete()
          .eq('plumber_id', editingPlumber.id);

        // Get city IDs for zip codes
        const { data: cityData } = await supabase
          .from('cities')
          .select('id, zip_codes')
          .filter('zip_codes', 'cs', `{${editingPlumber.service_areas.map(a => a.zip_code).join(',')}}`);

        const zipToCityMap = new Map();
        cityData?.forEach(city => {
          city.zip_codes.forEach((zip: string) => {
            zipToCityMap.set(zip, city.id);
          });
        });

        // Insert new service areas
        await supabase
          .from('plumber_service_areas')
          .insert(editingPlumber.service_areas.map(area => ({
            plumber_id: editingPlumber.id,
            zip_code: area.zip_code,
            city_id: zipToCityMap.get(area.zip_code)
          })));
      }

      toast.success('Plumber updated successfully');
      setEditingPlumber(null);
      loadPlumbers();
    } catch (error) {
      console.error('Error updating plumber:', error);
      toast.error('Failed to update plumber');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Manage Plumbers</h2>
        <button
          onClick={() => {
            setEditingPlumber(defaultPlumber);
            setShowForm(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Plumber
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Business
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reviews
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {plumbers.map(plumber => (
              <tr key={plumber.id}>
                {editingPlumber?.id === plumber.id ? (
                  <td colSpan={5} className="px-6 py-4">
                    <PlumberForm
                      plumber={editingPlumber}
                      onChange={(field, value) => setEditingPlumber(prev => ({ ...prev!, [field]: value }))}
                      onSubmit={handleEditSubmit}
                      onCancel={() => setEditingPlumber(null)}
                      isSubmitting={isSubmitting}
                    />
                  </td>
                ) : (
                  <>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {plumber.logo_url ? (
                      <img
                        src={plumber.logo_url}
                        alt={plumber.business_name}
                        className="h-10 w-10 rounded-full"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                    )}
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {plumber.business_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        License: {plumber.license_number || 'N/A'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{plumber.contact_name}</div>
                  <div className="text-sm text-gray-500">{plumber.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleVerification(plumber)}
                      className={`p-1 rounded-full ${
                        plumber.is_verified
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                      title={plumber.is_verified ? 'Verified' : 'Not verified'}
                    >
                      <Shield className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => toggleFeatured(plumber)}
                      className={`p-1 rounded-full ${
                        plumber.is_featured
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                      title={plumber.is_featured ? 'Featured' : 'Not featured'}
                    >
                      <Award className="w-5 h-5" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {plumber.reviews.length} reviews
                  </div>
                  <div className="text-sm text-gray-500">
                    {plumber.rating.toFixed(1)} average
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => setEditingPlumber(plumber)}
                    className="text-blue-600 hover:text-blue-900 mr-2"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deletePlumber(plumber)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && !editingPlumber?.id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <PlumberForm
              plumber={defaultPlumber}
              onChange={(field, value) => setEditingPlumber(prev => ({ ...prev!, [field]: value }))}
              onSubmit={handleEditSubmit}
              onCancel={() => { setShowForm(false); setEditingPlumber(null); }}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  );
}